/**
 * "Colour and categories" of an event (Outlook Categorize + Google event colour): pick categories (or create one with a
 * colour) and an own colour for the event. Categories are Outlook categories on the item; the list with colours is
 * the user's (categories.ts).
 */
import { useI18n } from '@platform/i18n';
import { Button, CALENDAR_COLOR_CLASSES, Checkbox, cn, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, useErrorText, type CalendarColor } from '@platform/ui';
import { useId, useState, type FormEvent } from 'react';
import { api, type CalendarEventScope } from '../api';
import { useCalendar } from './calendar-context';
import { categoryOf } from './categories';
import { ColorChoice } from './color-choice';
import type { ShownEvent } from './events';

export function CategorizeDialog({ item, scope, onClose }: { item: ShownEvent; scope: CalendarEventScope; onClose: () => void }) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const { categories, saveCategories, changed } = useCalendar();
  const ids = { name: useId() };
  const [chosen, setChosen] = useState<string[]>(item.event.categories);
  const [color, setColor] = useState<CalendarColor | null>(item.event.color);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<CalendarColor>('blue');
  const [busy, setBusy] = useState(false);
  const known = [...categories, ...chosen.filter((n) => !categoryOf(categories, n)).map((name) => ({ name, color: undefined }))];
  const has = (name: string) => chosen.some((n) => n.toLowerCase() === name.toLowerCase());

  const addCategory = () => {
    const name = newName.trim();
    if (!name) return;
    if (!categoryOf(categories, name)) saveCategories([...categories, { name, color: newColor }]);
    if (!has(name)) setChosen([...chosen, name]);
    setNewName('');
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.updateEvent(item.event.id, { categories: chosen, color }, scope, false);
      changed();
      onClose();
    } catch (error) {
      errorText.notify(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendarUx.categorize.title')}</DialogTitle>
          <DialogDescription>{item.grid.title}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)}>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium">{t('webmail.calendarUx.editor.categories')}</legend>
            {known.length === 0 && <p className="text-sm text-muted-foreground">{t('webmail.calendarUx.categorize.none')}</p>}
            {known.map((c) => (
              <label key={c.name} className="flex items-center gap-2 text-sm">
                <Checkbox checked={has(c.name)} onCheckedChange={(on) => setChosen(on === true ? [...chosen, c.name] : chosen.filter((n) => n.toLowerCase() !== c.name.toLowerCase()))} />
                <span aria-hidden className={cn('size-3 rounded-full border', c.color ? CALENDAR_COLOR_CLASSES[c.color].swatch : 'bg-background')} />
                {c.name}
              </label>
            ))}
            <div className="flex flex-col gap-2 rounded-md border p-2">
              <Label htmlFor={ids.name} className="font-normal">
                {t('webmail.calendarUx.categorize.new')}
              </Label>
              <div className="flex gap-2">
                <Input id={ids.name} value={newName} maxLength={255} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())} />
                <Button type="button" variant="outline" onClick={addCategory} disabled={!newName.trim()}>
                  {t('webmail.calendarUx.categorize.add')}
                </Button>
              </div>
              <ColorChoice small label={t('webmail.calendarUx.categorize.newColor')} value={newColor} onChange={(c) => c && setNewColor(c)} />
            </div>
          </fieldset>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium">{t('webmail.calendarUx.categorize.eventColor')}</legend>
            <ColorChoice small label={t('webmail.calendarUx.categorize.eventColor')} value={color} onChange={setColor} noneLabel={t('webmail.calendarUx.categorize.calendarColor')} />
          </fieldset>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={busy}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
